import React from 'react';
import { Icon, IconName } from './Icon';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style' | 'onClick'> {
  children: React.ReactNode;
  /** Visual style. Default 'secondary'. */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Height tier: 'md' = 64px, 'lg' = 72px. Default 'md'. */
  size?: 'md' | 'lg';
  /** Optional leading icon name. */
  icon?: IconName;
  /** Optional trailing icon name. */
  iconRight?: IconName;
  disabled?: boolean;
  /** Stretch to container width. */
  fullWidth?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  style?: React.CSSProperties;
}

/**
 * Button — standard tappable control. Field-sized (≥64px tall).
 * variant: 'primary' (calm blue), 'secondary' (white + border), 'ghost' (text only).
 * Use PrimaryButton for the oversized bottom-right confirm action.
 */
export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  icon,
  iconRight,
  disabled = false,
  fullWidth = false,
  onClick,
  style,
  ...rest
}: ButtonProps) {
  const heights: Record<'md' | 'lg', string> = { md: 'var(--touch-min)', lg: 'var(--segment-min)' };

  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-3)',
    minHeight: heights[size] || heights.md,
    padding: '0 var(--space-6)',
    width: fullWidth ? '100%' : undefined,
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--text-body)',
    fontWeight: 'var(--weight-semibold)',
    lineHeight: 1,
    borderRadius: 'var(--radius-md)',
    border: 'var(--border-width-strong) solid transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color .12s ease, transform .06s ease',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
  };

  const variants: Record<'primary' | 'secondary' | 'ghost', React.CSSProperties> = {
    primary: {
      background: disabled ? 'var(--action-disabled-bg)' : 'var(--action-primary)',
      color: disabled ? 'var(--action-disabled-text)' : 'var(--text-on-action)',
      borderColor: 'transparent',
    },
    secondary: {
      background: 'var(--surface-card)',
      color: disabled ? 'var(--text-disabled)' : 'var(--text-primary)',
      borderColor: 'var(--border-strong)',
    },
    ghost: {
      background: 'transparent',
      color: disabled ? 'var(--text-disabled)' : 'var(--action-primary)',
      borderColor: 'transparent',
    },
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      style={{ ...base, ...variants[variant], ...style }}
      onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = 'scale(0.98)'; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
      {...rest}
    >
      {icon ? <Icon name={icon} size={24} /> : null}
      <span>{children}</span>
      {iconRight ? <Icon name={iconRight} size={24} /> : null}
    </button>
  );
}
